import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject, UploadMetadata } from 'firebase/storage';
import { getAuth, getIdToken } from 'firebase/auth';
import app from '../firebase/config';

// Check if Firebase is configured
const isFirebaseConfigured = () => {
  return app && process.env.REACT_APP_FIREBASE_STORAGE_BUCKET && 
         process.env.REACT_APP_FIREBASE_STORAGE_BUCKET !== 'your_storage_bucket';
};

// Get current user info for debugging
export const getCurrentUserInfo = (): { uid: string | null; email: string | null; isAuthenticated: boolean } => {
  try {
    const auth = getAuth(app);
    const user = auth.currentUser;
    return {
      uid: user?.uid || null,
      email: user?.email || null,
      isAuthenticated: user !== null
    };
  } catch (error) {
    console.error('[ImageUpload] Error getting user info:', error);
    return {
      uid: null,
      email: null,
      isAuthenticated: false
    };
  }
};

/**
 * Upload an image file to Firebase Storage
 * @param file The image file to upload
 * @param path Optional custom path (default: 'images/{timestamp}-{filename}')
 * @returns Promise<string> The download URL of the uploaded image
 */
export const uploadImage = async (file: File, path?: string): Promise<string> => {
  if (!isFirebaseConfigured()) {
    // Fallback: create a data URL for local development
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    });
  }

  // Check if user is authenticated
  const userInfo = getCurrentUserInfo();
  if (!userInfo.isAuthenticated) {
    console.error('[ImageUpload] Upload attempted without authentication. User info:', userInfo);
    throw new Error(
      `You must be signed in to upload images. ` +
      `Current status: ${userInfo.isAuthenticated ? 'authenticated' : 'not authenticated'}. ` +
      `Please sign in and try again.`
    );
  }
  
  console.log('[ImageUpload] Starting upload for user:', userInfo.uid, 'File:', file.name, 'Size:', file.size);

  try {
    // Verify and refresh auth token if needed
    const auth = getAuth(app);
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('User authentication lost. Please sign in again.');
    }
    
    // Try to get the ID token to ensure it's valid
    try {
      const token = await getIdToken(currentUser, true); // Force refresh
      console.log('[ImageUpload] Auth token obtained. Token length:', token.length);
    } catch (tokenError) {
      console.error('[ImageUpload] Failed to get auth token:', tokenError);
      throw new Error('Failed to verify authentication token. Please sign in again.');
    }
    
    const storage = getStorage(app);
    
    // Generate path if not provided
    const storagePath = path || `images/${Date.now()}-${file.name}`;
    const storageRef = ref(storage, storagePath);
    
    // Prepare metadata with content type
    const metadata: UploadMetadata = {
      contentType: file.type || 'image/jpeg',
      customMetadata: {
        uploadedBy: currentUser.uid,
        uploadedAt: new Date().toISOString(),
        originalName: file.name
      }
    };
    
    console.log('[ImageUpload] Uploading to path:', storagePath, 'with metadata:', metadata);
    
    // Upload file with metadata
    await uploadBytes(storageRef, file, metadata);
    
    // Get download URL
    const downloadURL = await getDownloadURL(storageRef);
    
    return downloadURL;
  } catch (error: any) {
    console.error('Error uploading image:', error);
    
    // Provide more specific error messages
    if (error.code === 'storage/unauthorized') {
      const userInfo = getCurrentUserInfo();
      console.error('[ImageUpload] Unauthorized error. User info:', userInfo, 'Error:', error);
      throw new Error(
        `Permission denied (403). ` +
        `User ID: ${userInfo.uid || 'not authenticated'}, ` +
        `Email: ${userInfo.email || 'N/A'}, ` +
        `Authenticated: ${userInfo.isAuthenticated}. ` +
        `Please check your Firebase Storage security rules. ` +
        `Make sure authenticated users can write to 'images/' folder. ` +
        `See docs/FIREBASE_STORAGE_SETUP.md for configuration instructions.`
      );
    } else if (error.code === 'storage/canceled') {
      throw new Error('Upload was canceled.');
    } else if (error.code === 'storage/unknown') {
      throw new Error('An unknown error occurred. Please check your Firebase Storage configuration and CORS settings.');
    } else if (error.message?.includes('CORS')) {
      throw new Error('CORS error: Please configure Firebase Storage CORS rules or check your authentication status.');
    }
    
    throw new Error(error.message || 'Failed to upload image. Please try again.');
  }
};

/**
 * Delete an image from Firebase Storage
 * @param url The download URL of the image to delete
 */
export const deleteImage = async (url: string): Promise<void> => {
  if (!isFirebaseConfigured()) {
    return; // No-op in fallback mode
  }

  try {
    // Extract path from URL
    const urlObj = new URL(url);
    const path = decodeURIComponent(urlObj.pathname.split('/o/')[1]?.split('?')[0] || '');
    
    if (!path) {
      console.warn('Could not extract path from URL:', url);
      return;
    }

    const storage = getStorage(app);
    const storageRef = ref(storage, path);
    
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting image:', error);
    // Don't throw - deletion failures shouldn't block the user
  }
};

/**
 * Validate if a file is an image
 * @param file The file to validate
 * @returns boolean
 */
export const isValidImageFile = (file: File): boolean => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
  return validTypes.includes(file.type);
};

/**
 * Validate image file size (default max: 5MB)
 * @param file The file to validate
 * @param maxSizeMB Maximum size in MB (default: 5)
 * @returns boolean
 */
export const isValidImageSize = (file: File, maxSizeMB: number = 5): boolean => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

